import { UserRound, Loader2, ShieldAlert, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

const eur = (v) => (v == null ? "—" : `${Math.round(v / 1000).toLocaleString("fr-FR")} k€`);

export default function DirigeantCard({ dirigeant: d, checks, onVerify }) {
    return (
        <Card data-testid={`dirigeant-card-${d.nom_complet.replace(/\s+/g, "_")}`} className="border-slate-200 bg-white shadow-sm">
            <CardContent className="pt-6 space-y-5">
                <div className="flex flex-wrap items-start gap-4">
                    <div className="h-10 w-10 rounded-full bg-accent flex items-center justify-center">
                        <UserRound className="h-4 w-4 text-primary" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p data-testid="dirigeant-nom" className="font-medium text-foreground">{d.nom_complet}</p>
                        <p className="text-xs text-muted-foreground">
                            {d.qualite}{d.date_prise_de_poste ? ` depuis ${d.date_prise_de_poste}` : ""}{d.naissance ? ` · né(e) en ${d.naissance}` : ""}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Badge className="rounded-full bg-primary/10 text-primary border-transparent shadow-sm">
                            {d.nb_entreprises_total ?? d.entreprises.length + 1} mandat{(d.nb_entreprises_total ?? 0) > 1 ? "s" : ""}
                        </Badge>
                        {d.nb_cessees > 0 && (
                            <Badge data-testid="dirigeant-cessees" className="rounded-full bg-rose-100 text-rose-800 border-transparent shadow-sm">
                                {d.nb_cessees} société{d.nb_cessees > 1 ? "s" : ""} cessée{d.nb_cessees > 1 ? "s" : ""}
                            </Badge>
                        )}
                    </div>
                </div>

                {d.entreprises.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Aucun autre mandat connu.</p>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-[0.14em] text-muted-foreground border-b border-border">
                                <th className="py-2 pr-3 font-medium">Société</th>
                                <th className="py-2 pr-3 font-medium">Rôle</th>
                                <th className="py-2 pr-3 font-medium">Statut</th>
                                <th className="py-2 pr-3 font-medium text-right">CA</th>
                                <th className="py-2 pr-3 font-medium text-right">Résultat</th>
                                <th className="py-2 font-medium text-right">Procédures</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {d.entreprises.map((e) => {
                                const c = checks[e.siren];
                                return (
                                    <tr key={e.siren} data-testid={`mandat-row-${e.siren}`}>
                                        <td className="py-2.5 pr-3">
                                            <p className="font-medium text-foreground">{e.nom_entreprise}</p>
                                            <p className="text-xs text-muted-foreground">{e.forme_juridique}{e.ville ? ` · ${e.ville}` : ""} · SIREN {e.siren}</p>
                                        </td>
                                        <td className="py-2.5 pr-3 text-muted-foreground">{e.qualite || "—"}{e.actuel === false ? ` (ancien${e.date_depart ? `, départ ${e.date_depart}` : ""})` : ""}</td>
                                        <td className="py-2.5 pr-3">
                                            <span className={`text-xs font-medium ${e.entreprise_cessee ? "text-rose-700" : "text-emerald-700"}`}>
                                                {e.entreprise_cessee ? "Cessée" : e.statut_consolide || "Active"}
                                            </span>
                                        </td>
                                        <td className="py-2.5 pr-3 text-right tabular-nums">{eur(e.chiffre_affaires)}</td>
                                        <td className={`py-2.5 pr-3 text-right tabular-nums ${e.resultat < 0 ? "text-rose-700" : ""}`}>{eur(e.resultat)}</td>
                                        <td className="py-2.5 text-right">
                                            {!c ? (
                                                <button type="button" data-testid={`verify-${e.siren}`} onClick={() => onVerify(e.siren)} className="text-xs text-primary underline-offset-2 hover:underline">
                                                    Vérifier
                                                </button>
                                            ) : c.loading ? (
                                                <Loader2 className="h-4 w-4 animate-spin inline text-muted-foreground" />
                                            ) : c.error ? (
                                                <span className="text-xs text-destructive">Erreur</span>
                                            ) : c.procedure_en_cours || c.procedures.length > 0 ? (
                                                <span data-testid={`proc-alert-${e.siren}`} className="inline-flex items-center gap-1 text-xs text-amber-700"><ShieldAlert className="h-3.5 w-3.5" /> {c.procedures.length || 1}</span>
                                            ) : (
                                                <span data-testid={`proc-ok-${e.siren}`} className="inline-flex items-center gap-1 text-xs text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" /> Aucune</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </CardContent>
        </Card>
    );
}
